/**
 * Generic DataLoader to batch multiple individual ID requests into a single batch request.
 *
 * @internal
 */
export class BatchLoader<T extends { id: number }> {
  private queue = new Map<number, { resolve: (val: T) => void; reject: (err: unknown) => void }[]>();
  private timeout: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly batchFetch: (ids: number[]) => Promise<T[]>,
    private readonly maxWaitMs: number = 50,
    private readonly maxBatchSize: number = 50,
  ) {}

  /**
   * Queue an ID to be fetched in the next batch.
   * Returns a Promise that resolves when the batch request completes.
   */
  async load(id: number): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      let callbacks = this.queue.get(id);
      if (!callbacks) {
        callbacks = [];
        this.queue.set(id, callbacks);
      }
      callbacks.push({ resolve, reject });

      if (this.queue.size >= this.maxBatchSize) {
        if (this.timeout) {
          clearTimeout(this.timeout);
          this.timeout = null;
        }
        // Dispatch immediately without waiting
        // We use void to ignore the returned promise
        void this.dispatch();
        return;
      }

      if (this.timeout === null) {
        this.timeout = setTimeout(() => {
          void this.dispatch();
        }, this.maxWaitMs);
        const timer = this.timeout as unknown as { unref?: () => void };
        if (typeof timer.unref === "function") {
          timer.unref(); // Prevent blocking the event loop in Node.js
        }
      }
    });
  }

  private async dispatch() {
    this.timeout = null;
    if (this.queue.size === 0) return;

    const currentQueue = this.queue;
    this.queue = new Map(); // Reset queue for subsequent requests

    const ids = Array.from(currentQueue.keys());

    try {
      const results = await this.batchFetch(ids);
      const resultMap = new Map<number, T>();
      for (const item of results) {
        resultMap.set(item.id, item);
      }

      for (const [id, callbacks] of currentQueue.entries()) {
        const result = resultMap.get(id);
        if (result) {
          for (const cb of callbacks) cb.resolve(result);
        } else {
          const err = new Error(`Item with ID ${id} not found in batch response`);
          for (const cb of callbacks) cb.reject(err);
        }
      }
    } catch (err) {
      // Propagate the error to all queued promises
      for (const callbacks of currentQueue.values()) {
        for (const cb of callbacks) cb.reject(err);
      }
    }
  }
}
